import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  LayoutDashboard,
  GraduationCap,
  BookOpen,
  ClipboardList,
  CalendarCheck,
  CreditCard,
  Clock,
  MessageSquare,
  Users,
  Library,
  Bus,
  Building2,
  BarChart3,
  Settings,
  LogOut,
  Lock,
  UserPlus,
  Package,
  Heart,
  Shield,
  ChevronsUpDown,
  School,
  ChevronRight,
  BookMarked,
  ClipboardCheck,
  ReceiptText,
  Banknote,
  Layers,
  CalendarDays,
  TrendingUp,
  Megaphone,
  UserCog,
  ShieldCheck,
  Stethoscope,
  CalendarClock,
  Home,
  MapPin,
  FileText,
  BarChart2,
  Activity,
  Truck,
  BookCopy,
  BookX,
  Wrench,
  CalendarRange,
  LayoutGrid,
  Bell,
  BarChart,
  Calendar,
  Star,
  RefreshCw,
  FileBadge,
  ClipboardSignature,
  ShieldAlert,
  BookUser,
  UserCircle,
  Navigation,
  Scale,
  Award,
  ShoppingCart,
  DollarSign,
  Pin,
  UserCheck,
  Repeat,
  BellRing,
  Calculator,
  CheckSquare,
  Trophy,
  IdCard,
  UtensilsCrossed,
  DoorOpen,
  Target,
  Briefcase,
  FolderOpen,
  Repeat2,
  ShieldCheck as ShieldCheckIcon,
  Wallet,
  Contact,
  Presentation,
  Headphones,
  Receipt,
  ClipboardPen,
  HeartPulse,
  BookOpenCheck,
  ScanBarcode,
  Bookmark,
  Newspaper,
  Monitor,
  ClipboardX,
  Gamepad2,
  MessageCircle,
  ArrowRightLeft,
  GraduationCap as GradCapIcon,
  CalendarHeart,
  Send,
  Phone,
  SlidersHorizontal,
  QrCode,
  LibraryBig,
  UserSquare2,
  HardDrive,
  Trash2,
  ListChecks,
  Wand2,
  PenLine,
  Brain,
  Radio,
  Network,
  ScrollText,
  Globe,
  Rocket,
  LifeBuoy,
  ToggleLeft,
  KeyRound,
  AlertTriangle,
  Video,
  GitMerge,
  FlaskConical,
  Workflow,
  LineChart,
  Zap,
  Sparkles,
  Leaf,
  HardHat,
  ScanLine,
  UserX,
  Route,
  History,
  PackageSearch,
  Fence,
  Car,
  Siren,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuthStore, logoutWithServer } from "@/stores/auth.store";
import { P, hasPermission } from "@/lib/permissions";
import { useUIStore, type SidebarStyle } from "@/stores/ui.store";
import { useTenant } from "@/features/tenant/api/tenant.queries";

const HE_SCHOOL_TYPES = new Set(["university", "college", "tvet"]);

// ── Types ──────────────────────────────────────────────────────────────────

interface SubItem {
  labelKey: string;
  url: string;
  icon: React.ElementType;
  permission?: string;
  group?: string; // section divider label rendered above this item
}

interface NavItem {
  titleKey: string;
  url: string;
  icon: React.ElementType;
  permission?: string;
  children?: SubItem[];
  external?: boolean; // open in a new tab (for public portals)
}

// ── Nav tree ───────────────────────────────────────────────────────────────

const mainNav: NavItem[] = [
  { titleKey: "dashboard", url: "/", icon: LayoutDashboard },
  {
    titleKey: "students",
    url: "/students",
    icon: GraduationCap,
    permission: P.STUDENTS_READ,
  },
  {
    titleKey: "academics",
    url: "/academics",
    icon: BookOpen,
    permission: P.CLASSES_READ,
    children: [
      { labelKey: "classes", url: "/academics/classes", icon: Layers, permission: P.CLASSES_READ },
      { labelKey: "streams", url: "/academics/streams", icon: GitMerge, permission: P.CLASSES_READ },
      { labelKey: "subjects", url: "/academics/subjects", icon: BookMarked, permission: P.SUBJECTS_READ },
      { labelKey: "subjectCombinations", url: "/academics/subject-combinations", icon: FlaskConical, permission: P.SUBJECTS_READ },
      { labelKey: "departments", url: "/academics/departments", icon: Building2, permission: P.DEPARTMENTS_READ },
      { labelKey: "academicYears", url: "/academics/years", icon: CalendarDays, permission: P.ACADEMIC_YEARS_READ },
      { labelKey: "academicCalendar", url: "/academics/calendar", icon: CalendarRange, permission: P.ACADEMIC_YEARS_READ },
      { labelKey: "gradingScales", url: "/academics/grading-scales", icon: TrendingUp, permission: P.GRADES_READ },
      { labelKey: "promotionRules", url: "/academics/promotion-rules", icon: Workflow, permission: P.ACADEMIC_YEARS_WRITE },
      { labelKey: "curriculum", url: "/academics/curriculum", icon: Brain, permission: P.SUBJECTS_READ },
      { labelKey: "academicAnalytics", url: "/academics/analytics", icon: LineChart, permission: P.REPORTS_READ },
    ],
  },
  {
    titleKey: "assessments",
    url: "/assessments",
    icon: ClipboardList,
    permission: P.ASSESSMENTS_READ,
    children: [
      { labelKey: "assessments", url: "/assessments", icon: ClipboardList, permission: P.ASSESSMENTS_READ },
      { labelKey: "gradebook", url: "/assessments/gradebook", icon: ClipboardCheck, permission: P.GRADES_READ },
      { labelKey: "exams", url: "/exams", icon: FileText, permission: P.EXAMS_READ },
    ],
  },
  {
    titleKey: "attendance",
    url: "/attendance",
    icon: CalendarCheck,
    permission: P.ATTENDANCE_READ,
    children: [
      { labelKey: "overview", url: "/attendance", icon: Activity, permission: P.ATTENDANCE_READ },
      { labelKey: "markAttendance", url: "/attendance/mark", icon: CalendarCheck, permission: P.ATTENDANCE_WRITE },
      { labelKey: "gateCheckIn", url: "/attendance/gate", icon: DoorOpen, permission: P.ATTENDANCE_WRITE },
      { labelKey: "studentGroups", url: "/attendance/groups", icon: Users, permission: P.ATTENDANCE_READ },
      { labelKey: "idCards", url: "/attendance/id-cards", icon: IdCard, permission: P.ATTENDANCE_READ },
      { labelKey: "register", url: "/attendance/register", icon: ClipboardSignature, permission: P.ATTENDANCE_WRITE },
      { labelKey: "reports", url: "/attendance/reports", icon: BarChart2, permission: P.ATTENDANCE_READ },
    ],
  },
  {
    titleKey: "fees",
    url: "/fees",
    icon: CreditCard,
    permission: P.FEES_READ,
    children: [
      { labelKey: "feeDashboard", url: "/fees/dashboard", icon: BarChart, permission: P.FEES_READ },
      { labelKey: "feeStructures", url: "/fees/structures", icon: Layers, permission: P.FEES_READ },
      { labelKey: "invoices", url: "/fees/invoices", icon: ReceiptText, permission: P.INVOICES_READ },
      { labelKey: "payments", url: "/fees/payments", icon: Banknote, permission: P.PAYMENTS_READ },
      { labelKey: "feeWaivers", url: "/fees/waivers", icon: Award, permission: P.FEES_READ },
      { labelKey: "arAging", url: "/fees/aging", icon: Clock, permission: P.FEES_READ },
    ],
  },
  {
    titleKey: "accounting",
    url: "/accounting",
    icon: BookOpen,
    permission: P.FEES_READ,
    children: [
      { labelKey: "chartOfAccounts", url: "/accounting/accounts", icon: BookOpen, permission: P.FEES_READ },
      { labelKey: "journalEntries", url: "/accounting/journal", icon: ClipboardList, permission: P.FEES_READ },
      { labelKey: "trialBalance", url: "/accounting/trial-balance", icon: Scale, permission: P.FEES_READ },
      { labelKey: "incomeStatement", url: "/accounting/income-statement", icon: TrendingUp, permission: P.FEES_READ },
      { labelKey: "budgetVsActuals", url: "/accounting/budget", icon: BarChart2, permission: P.FEES_READ },
    ],
  },
  {
    titleKey: "timetable",
    url: "/timetable",
    icon: Clock,
    permission: P.TIMETABLE_READ,
    children: [
      { labelKey: "view", url: "/timetable", icon: Clock, permission: P.TIMETABLE_READ },
      { labelKey: "builder", url: "/timetable/builder", icon: CalendarRange, permission: P.TIMETABLE_WRITE },
    ],
  },
  { titleKey: "events", url: "/events", icon: Calendar, permission: P.EVENTS_READ },
  {
    titleKey: "lms",
    url: "/lms",
    icon: Monitor,
    permission: P.LMS_READ,
    children: [
      // ── Overview ────────────────────────────────
      { labelKey: "lmsDashboard",          url: "/lms",                      icon: LayoutDashboard, permission: P.LMS_READ },
      { labelKey: "lmsNotifications",      url: "/lms/notifications",        icon: Bell,            permission: P.LMS_READ },

      // ── Learn ────────────────────────────────────
      { group: "learn", labelKey: "myLearning",          url: "/lms/my-learning",          icon: GraduationCap,   permission: P.LMS_READ },
      { labelKey: "courses",               url: "/lms/courses",              icon: BookOpen,        permission: P.LMS_READ },
      { labelKey: "learningPaths",         url: "/lms/learning-paths",       icon: Layers,          permission: P.LMS_READ },
      { labelKey: "liveClasses",           url: "/lms/live-classes",         icon: Radio,           permission: P.LMS_READ },
      { labelKey: "myAssignments",         url: "/lms/my-assignments",       icon: ClipboardList,   permission: P.LMS_READ },
      { labelKey: "myQuizzes",             url: "/lms/my-quizzes",           icon: ClipboardCheck,  permission: P.LMS_READ },
      { labelKey: "discussions",           url: "/lms/discussions",          icon: MessageSquare,   permission: P.LMS_READ },
      { labelKey: "certificates",          url: "/lms/certificates",         icon: Award,           permission: P.LMS_READ },
      { labelKey: "gamification",          url: "/lms/gamification",         icon: Trophy,          permission: P.LMS_READ },

      // ── Teach ─────────────────────────────────────
      { group: "teach", labelKey: "instructorLMSCourses", url: "/lms/my-courses",          icon: PenLine,         permission: P.LMS_WRITE },
      { labelKey: "courseBuilder",         url: "/lms/courses",              icon: Wand2,           permission: P.LMS_WRITE },
      { labelKey: "lmsGradebook",          url: "/lms/gradebook",            icon: BarChart3,       permission: P.LMS_WRITE },
      { labelKey: "lmsSubmissions",        url: "/lms/submissions",          icon: ClipboardList,   permission: P.LMS_WRITE },
      { labelKey: "questionBank",          url: "/lms/question-bank",        icon: Brain,           permission: P.LMS_WRITE },
      { labelKey: "rubrics",               url: "/lms/rubrics",              icon: ScrollText,      permission: P.LMS_WRITE },
      { labelKey: "contentLibrary",        url: "/lms/content-library",      icon: HardDrive,       permission: P.LMS_WRITE },
      { labelKey: "scormContent",          url: "/lms/scorm",                icon: Layers,          permission: P.LMS_WRITE },

      // ── Manage ────────────────────────────────────
      { group: "manage", labelKey: "enrollmentManagement", url: "/lms/enrollments",         icon: UserCheck,       permission: P.LMS_MANAGE },
      { labelKey: "competencies",          url: "/lms/competencies",         icon: Network,         permission: P.LMS_MANAGE },
      { labelKey: "lmsAnalytics",          url: "/lms/analytics",            icon: BarChart2,       permission: P.LMS_MANAGE },
      { labelKey: "lmsReports",            url: "/lms/reports",              icon: FileText,        permission: P.LMS_MANAGE },
      { labelKey: "lmsSettings",           url: "/lms/settings",             icon: Settings,        permission: P.LMS_MANAGE },
    ],
  },
];

const secondaryNav: NavItem[] = [
  {
    titleKey: "communications",
    url: "/communications",
    icon: MessageSquare,
    permission: P.MESSAGES_READ,
    children: [
      { labelKey: "messages", url: "/communications/messages", icon: MessageSquare, permission: P.MESSAGES_READ },
      { labelKey: "announcements", url: "/communications/announcements", icon: Megaphone, permission: P.ANNOUNCEMENTS_READ },
      { labelKey: "notifications", url: "/communications/notifications", icon: Bell, permission: P.MESSAGES_WRITE },
      { labelKey: "notificationProviders", url: "/communications/sms-providers", icon: Phone, permission: P.SETTINGS_MANAGE },
      { labelKey: "deliveryQueue", url: "/communications/dlq", icon: Send, permission: P.SETTINGS_MANAGE },
    ],
  },
  {
    titleKey: "hr",
    url: "/hr",
    icon: Users,
    permission: P.EMPLOYEES_READ,
    children: [
      { labelKey: "hrDashboard", url: "/hr", icon: LayoutDashboard, permission: P.EMPLOYEES_READ },
      { labelKey: "employees", url: "/hr/employees", icon: Users, permission: P.EMPLOYEES_READ },
      { labelKey: "leaveRequests", url: "/hr/leave", icon: CalendarClock, permission: P.EMPLOYEES_READ },
      { labelKey: "staffAttendance", url: "/hr/staff-attendance", icon: ClipboardCheck, permission: P.EMPLOYEES_READ },
      { labelKey: "appraisals", url: "/hr/appraisals", icon: Star, permission: P.EMPLOYEES_READ },
      { labelKey: "expenses", url: "/hr/expenses", icon: Receipt, permission: P.EMPLOYEES_READ },
      { labelKey: "disciplinary", url: "/hr/disciplinary", icon: Scale, permission: P.EMPLOYEES_READ },
      { labelKey: "onboarding", url: "/hr/onboarding", icon: UserCog, permission: P.EMPLOYEES_READ },
      { labelKey: "payroll", url: "/hr/payroll", icon: Banknote, permission: P.PAYROLL_READ },
    ],
  },
  {
    titleKey: "admissions",
    url: "/admissions",
    icon: UserPlus,
    permission: P.ADMISSIONS_READ,
    children: [
      { labelKey: "admissionsDashboard", url: "/admissions/dashboard", icon: LayoutDashboard, permission: P.ADMISSIONS_READ },
      { labelKey: "admissionCycles", url: "/admissions/cycles", icon: CalendarRange, permission: P.ADMISSIONS_READ },
      { labelKey: "allApplications", url: "/admissions", icon: ClipboardList, permission: P.ADMISSIONS_READ },
      { labelKey: "entranceTests", url: "/admissions/tests", icon: FileText, permission: P.ADMISSIONS_READ },
      { labelKey: "interviews", url: "/admissions/interviews", icon: Users, permission: P.ADMISSIONS_READ },
      { labelKey: "waitlist", url: "/admissions/waitlist", icon: Clock, permission: P.ADMISSIONS_READ },
      { labelKey: "admissionReports", url: "/admissions/reports", icon: BarChart3, permission: P.ADMISSIONS_READ },
    ],
  },
  {
    titleKey: "library",
    url: "/library",
    icon: Library,
    permission: P.LIBRARY_READ,
    children: [
      { labelKey: "libraryDashboard", url: "/library/dashboard",    icon: LayoutDashboard, permission: P.LIBRARY_READ },
      { labelKey: "catalog",          url: "/library",               icon: BookCopy,        permission: P.LIBRARY_READ },
      { labelKey: "returns",          url: "/library/returns",       icon: RefreshCw,       permission: P.LIBRARY_READ },
      { labelKey: "overdueBooks",     url: "/library/overdue",       icon: BookX,           permission: P.LIBRARY_READ },
      { labelKey: "libraryMembers",   url: "/library/members",       icon: Users,           permission: P.LIBRARY_READ },
      { labelKey: "libraryReservations", url: "/library/reservations", icon: Calendar,      permission: P.LIBRARY_READ },
      { labelKey: "libraryFines",     url: "/library/fines",         icon: DollarSign,      permission: P.LIBRARY_READ },
      { labelKey: "libraryAcquisitions", url: "/library/acquisitions", icon: ShoppingCart,  permission: P.LIBRARY_MANAGE },
      { labelKey: "libraryReports",   url: "/library/reports",       icon: BarChart3,       permission: P.LIBRARY_READ },
    ],
  },
  {
    titleKey: "transport",
    url: "/transport",
    icon: Bus,
    permission: P.TRANSPORT_READ,
    children: [
      { labelKey: "fleetDashboard", url: "/transport",              icon: LayoutDashboard,  permission: P.TRANSPORT_READ },
      { labelKey: "vehicles",       url: "/transport/vehicles",      icon: Truck,            permission: P.TRANSPORT_READ },
      { labelKey: "compliance",     url: "/transport/compliance",    icon: ShieldCheck,      permission: P.TRANSPORT_READ },
      { labelKey: "routes",         url: "/transport/routes",        icon: Navigation,       permission: P.TRANSPORT_READ },
      { labelKey: "assignments",    url: "/transport/assignments",   icon: Users,            permission: P.TRANSPORT_READ },
      { labelKey: "trips",          url: "/transport/trips",         icon: CalendarClock,    permission: P.TRANSPORT_READ },
      { labelKey: "tripSchedule",   url: "/transport/trips/schedule", icon: Repeat,          permission: P.TRANSPORT_DISPATCH },
      { labelKey: "liveTracking",   url: "/transport/tracking",      icon: MapPin,           permission: P.TRANSPORT_TRACK },
      { labelKey: "drivers",             url: "/transport/drivers",       icon: UserCheck,        permission: P.TRANSPORT_READ },
      { labelKey: "driverLogs",          url: "/transport/driver-logs",  icon: ClipboardList,    permission: P.TRANSPORT_READ },
      { labelKey: "vehicleMaintenance",  url: "/transport/maintenance",  icon: Wrench,           permission: P.TRANSPORT_READ },
      { labelKey: "fuelManagement",      url: "/transport/fuel",         icon: DollarSign,       permission: P.TRANSPORT_READ },
      { labelKey: "fuelAnalytics",       url: "/transport/fuel-analytics", icon: TrendingUp,     permission: P.TRANSPORT_READ },
      { labelKey: "transportIncidents",  url: "/transport/incidents",    icon: ShieldAlert,      permission: P.TRANSPORT_READ },
      { labelKey: "transportAlerts",     url: "/transport/alerts",       icon: BellRing,         permission: P.TRANSPORT_READ },
      { labelKey: "transportReports",    url: "/transport/reports",      icon: BarChart2,        permission: P.TRANSPORT_READ },
    ],
  },
  {
    titleKey: "facilities",
    url: "/facilities",
    icon: LayoutGrid,
    permission: P.FACILITIES_READ,
    children: [
      { labelKey: "overview", url: "/facilities", icon: Activity, permission: P.FACILITIES_READ },
      { labelKey: "buildings", url: "/facilities/buildings", icon: Building2, permission: P.FACILITIES_READ },
      { labelKey: "spaces", url: "/facilities/spaces", icon: Building2, permission: P.FACILITIES_READ },
      { labelKey: "bookings", url: "/facilities/bookings", icon: CalendarRange, permission: P.FACILITIES_READ },
      { labelKey: "maintenance", url: "/facilities/maintenance", icon: Wrench, permission: P.FACILITIES_READ },
      { labelKey: "energyMeters", url: "/facilities/energy-meters", icon: Zap, permission: P.FACILITIES_READ },
      { labelKey: "inspections", url: "/facilities/inspections", icon: ClipboardCheck, permission: P.FACILITIES_READ },
      { labelKey: "keys", url: "/facilities/keys", icon: KeyRound, permission: P.FACILITIES_READ },
      { labelKey: "venueHire", url: "/facilities/venue-hire", icon: CalendarCheck, permission: P.FACILITIES_READ },
      { labelKey: "cleaning", url: "/facilities/cleaning", icon: Sparkles, permission: P.FACILITIES_READ },
      { labelKey: "grounds", url: "/facilities/grounds", icon: Leaf, permission: P.FACILITIES_READ },
      { labelKey: "capitalProjects", url: "/facilities/capital-projects", icon: HardHat, permission: P.FACILITIES_READ },
      { labelKey: "accessControl", url: "/facilities/access-control", icon: ScanLine, permission: P.FACILITIES_READ },
    ],
  },
  {
    titleKey: "hostel",
    url: "/hostel",
    icon: Building2,
    permission: P.HOSTEL_READ,
    children: [
      { labelKey: "hostelOverview", url: "/hostel/overview", icon: LayoutGrid, permission: P.HOSTEL_READ },
      { labelKey: "hostelManagement", url: "/hostel/management", icon: Building2, permission: P.HOSTEL_MANAGE },
      { labelKey: "rooms", url: "/hostel/rooms", icon: Home, permission: P.HOSTEL_READ },
      { labelKey: "allocations", url: "/hostel/allocations", icon: Users, permission: P.HOSTEL_READ },
      { labelKey: "hostelMaintenance", url: "/hostel/maintenance", icon: Wrench, permission: P.HOSTEL_READ },
      { labelKey: "hostelVisitors", url: "/hostel/visitors", icon: UserCheck, permission: P.HOSTEL_READ },
      { labelKey: "hostelExeats", url: "/hostel/exeats", icon: LogOut, permission: P.HOSTEL_READ },
      { labelKey: "hostelFees", url: "/hostel/fees", icon: DollarSign, permission: P.HOSTEL_READ },
      { labelKey: "hostelNotices", url: "/hostel/notices", icon: Bell, permission: P.HOSTEL_READ },
      { labelKey: "hostelInspections", url: "/hostel/inspections", icon: ClipboardCheck, permission: P.HOSTEL_READ },
      { labelKey: "hostelRollCall", url: "/hostel/roll-call", icon: ListChecks, permission: P.HOSTEL_READ },
      { labelKey: "hostelIncidents", url: "/hostel/incidents", icon: ShieldAlert, permission: P.HOSTEL_READ },
    ],
  },
  {
    titleKey: "inventory",
    url: "/inventory",
    icon: Package,
    permission: P.INVENTORY_READ,
    children: [
      { labelKey: "inventoryOverview", url: "/inventory", icon: LayoutGrid, permission: P.INVENTORY_READ },
      { labelKey: "items", url: "/inventory/items", icon: Package, permission: P.INVENTORY_READ },
      { labelKey: "inventoryIssues", url: "/inventory/issues", icon: ClipboardList, permission: P.INVENTORY_READ },
      { labelKey: "requisitions", url: "/inventory/requisitions", icon: ClipboardCheck, permission: P.INVENTORY_READ },
      { labelKey: "inventoryMovements", url: "/inventory/movements", icon: BarChart2, permission: P.INVENTORY_READ },
      { labelKey: "stocktake", url: "/inventory/stocktake", icon: ClipboardCheck, permission: P.INVENTORY_MANAGE },
      { labelKey: "suppliers", url: "/inventory/suppliers", icon: Building2, permission: P.INVENTORY_READ },
      { labelKey: "purchaseOrders", url: "/inventory/purchase-orders", icon: Truck, permission: P.INVENTORY_READ },
      { labelKey: "warehouses", url: "/inventory/warehouses", icon: Building2, permission: P.INVENTORY_READ },
      { labelKey: "assetRegister", url: "/inventory/assets", icon: HardDrive, permission: P.INVENTORY_READ },
      { labelKey: "disposals", url: "/inventory/disposals", icon: Trash2, permission: P.INVENTORY_MANAGE },
      { labelKey: "inventoryReports", url: "/inventory/reports", icon: BarChart3, permission: P.INVENTORY_READ },
    ],
  },
  {
    titleKey: "health",
    url: "/health",
    icon: Heart,
    permission: P.HEALTH_READ,
    children: [
      { labelKey: "healthProfiles", url: "/health", icon: Heart, permission: P.HEALTH_READ },
      { labelKey: "clinicVisits", url: "/health/visits", icon: Stethoscope, permission: P.HEALTH_READ },
    ],
  },
  { titleKey: "discipline", url: "/discipline", icon: Shield, permission: P.DISCIPLINE_READ },
  {
    titleKey: "usalama",
    url: "/usalama",
    icon: ShieldAlert,
    permission: P.USALAMA_READ,
    children: [
      { labelKey: "usalamaDashboard",    url: "/usalama",                   icon: LayoutDashboard, permission: P.USALAMA_READ },
      { labelKey: "visitors",            url: "/usalama/visitors",          icon: UserPlus,        permission: P.USALAMA_READ },
      { labelKey: "incidents",           url: "/usalama/incidents",         icon: AlertTriangle,   permission: P.USALAMA_READ },
      { labelKey: "emergencies",         url: "/usalama/emergencies",       icon: BellRing,        permission: P.USALAMA_READ },
      { labelKey: "cameras",             url: "/usalama/cameras",           icon: Video,           permission: P.USALAMA_READ },
      { labelKey: "sensors",             url: "/usalama/sensors",           icon: Activity,        permission: P.USALAMA_READ },
      { labelKey: "rollCall",            url: "/usalama/roll-call",         icon: ListChecks,      permission: P.USALAMA_READ },
      { labelKey: "guards",              url: "/usalama/guards",            icon: Shield,          permission: P.USALAMA_READ },
      { labelKey: "tips",                url: "/usalama/tips",              icon: MessageSquare,   permission: P.USALAMA_READ },
      { labelKey: "behavioralConcerns",  url: "/usalama/concerns",          icon: Brain,           permission: P.USALAMA_READ },
      { labelKey: "examSecurity",        url: "/usalama/exam-security",     icon: FileText,        permission: P.USALAMA_READ },
      { labelKey: "safetyAudits",        url: "/usalama/safety-audits",     icon: ClipboardCheck,  permission: P.USALAMA_READ },
      { labelKey: "accessControl",       url: "/usalama/access-control",    icon: Lock,            permission: P.USALAMA_READ },
      { labelKey: "watchlist",            url: "/usalama/watchlist",          icon: UserX,           permission: P.USALAMA_READ },
      { labelKey: "accessPoints",         url: "/usalama/access-points",      icon: DoorOpen,        permission: P.USALAMA_READ },
      { labelKey: "protocols",            url: "/usalama/protocols",          icon: BookOpen,        permission: P.USALAMA_READ },
      { labelKey: "patrols",              url: "/usalama/patrols",            icon: Route,           permission: P.USALAMA_READ },
      { labelKey: "accessEvents",         url: "/usalama/access-events",      icon: History,         permission: P.USALAMA_READ },
      { labelKey: "visitorBadges",        url: "/usalama/visitor-badges",     icon: IdCard,          permission: P.USALAMA_READ },
      { labelKey: "pickupAuthorization",  url: "/usalama/pickup-authorization", icon: UserCheck,     permission: P.USALAMA_READ },
      { labelKey: "lostFound",            url: "/usalama/lost-found",         icon: PackageSearch,   permission: P.USALAMA_READ },
      { labelKey: "threatAssessment",     url: "/usalama/threat-assessment",  icon: ShieldAlert,     permission: P.USALAMA_READ },
      { labelKey: "headcount",            url: "/usalama/headcount",          icon: Users,           permission: P.USALAMA_READ },
      { labelKey: "perimeter",            url: "/usalama/perimeter",          icon: Fence,           permission: P.USALAMA_READ },
      { labelKey: "securityCompanies",    url: "/usalama/security-companies", icon: Building2,       permission: P.USALAMA_READ },
      { labelKey: "vehicles",             url: "/usalama/vehicles",           icon: Car,             permission: P.USALAMA_READ },
      { labelKey: "guardAttendance",      url: "/usalama/guard-attendance",   icon: Clock,           permission: P.USALAMA_READ },
      { labelKey: "drills",               url: "/usalama/drills",             icon: Siren,           permission: P.USALAMA_READ },
      { labelKey: "selfReportQR",         url: "/usalama/self-report-qr",     icon: QrCode,          permission: P.USALAMA_READ },
      { labelKey: "broadcasts",           url: "/usalama/broadcasts",         icon: Radio,           permission: P.USALAMA_READ },
    ],
  },
  {
    titleKey: "documents",
    url: "/documents",
    icon: FolderOpen,
    permission: P.DOCUMENTS_READ,
    children: [
      { labelKey: "allDocuments",   url: "/documents",                   icon: FileText,     permission: P.DOCUMENTS_READ },
      { labelKey: "uploadDocument", url: "/documents?upload=true",       icon: Pin,          permission: P.DOCUMENTS_WRITE },
      { labelKey: "docPolicies",    url: "/documents?category=policy",   icon: ScrollText,   permission: P.DOCUMENTS_READ },
      { labelKey: "docAcademic",    url: "/documents?category=academic", icon: GraduationCap, permission: P.DOCUMENTS_READ },
      { labelKey: "docFinancial",   url: "/documents?category=financial",icon: DollarSign,   permission: P.DOCUMENTS_READ },
      { labelKey: "docHr",          url: "/documents?category=hr",       icon: Users,        permission: P.DOCUMENTS_READ },
    ],
  },
];

const analyticsNav: NavItem[] = [
  { titleKey: "analytics", url: "/analytics", icon: BarChart, permission: P.REPORTS_READ },
  {
    titleKey: "reports",
    url: "/reports",
    icon: BarChart3,
    permission: P.REPORTS_READ,
    children: [
      { labelKey: "catalog", url: "/reports", icon: FileText, permission: P.REPORTS_READ },
      { labelKey: "reportCards", url: "/report-cards", icon: FileBadge, permission: P.REPORTS_READ },
    ],
  },
  { titleKey: "audit", url: "/audit", icon: ShieldAlert, permission: P.SETTINGS_MANAGE },
];

const portalNav: NavItem[] = [
  {
    titleKey: "studentPortal",
    url: "/student-portal",
    icon: UserCircle,
    permission: P.STUDENT_PORTAL_READ,
    children: [
      { labelKey: "studentDashboard",  url: "/student-portal",             icon: LayoutDashboard, permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentGrades",     url: "/student-portal/grades",      icon: GraduationCap,   permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentTimetable",  url: "/student-portal/timetable",   icon: Clock,           permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentAttendance", url: "/student-portal/attendance",  icon: CalendarCheck,   permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentFees",       url: "/student-portal/fees",        icon: CreditCard,      permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentExams",      url: "/student-portal/exams",       icon: ClipboardList,   permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentLibrary",    url: "/student-portal/library",     icon: Library,         permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentMessages",   url: "/student-portal/messages",    icon: MessageSquare,   permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentDocuments",    url: "/student-portal/documents",     icon: FileText,        permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentProfile",      url: "/student-portal/profile",       icon: UserCircle,      permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentAssignments",  url: "/student-portal/assignments",   icon: ClipboardList,   permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentGradeCalc",    url: "/student-portal/grade-calc",    icon: Calculator,      permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentStudyPlanner", url: "/student-portal/study-planner", icon: CheckSquare,     permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentTransport",    url: "/student-portal/transport",     icon: Bus,             permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentHostel",       url: "/student-portal/hostel",        icon: Building2,       permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentClubs",        url: "/student-portal/clubs",         icon: Trophy,          permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentCalendar",     url: "/student-portal/calendar",      icon: Calendar,        permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentIdCard",       url: "/student-portal/id-card",       icon: IdCard,          permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentMeals",        url: "/student-portal/meals",         icon: UtensilsCrossed, permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentCounseling",   url: "/student-portal/counseling",    icon: Heart,           permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentLeave",        url: "/student-portal/leave",         icon: DoorOpen,        permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentGoals",        url: "/student-portal/goals",         icon: Target,          permission: P.STUDENT_PORTAL_READ },
      { labelKey: "studentStudyGroups",  url: "/student-portal/study-groups",  icon: Users,           permission: P.STUDENT_PORTAL_READ },
    ],
  },
  {
    titleKey: "staffPortal",
    url: "/staff-portal",
    icon: Briefcase,
    permission: P.STAFF_PORTAL_READ,
    children: [
      { labelKey: "staffDashboard",      url: "/staff-portal",               icon: LayoutDashboard, permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffPayslips",       url: "/staff-portal/payslips",      icon: Receipt,         permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffLeave",          url: "/staff-portal/leave",         icon: DoorOpen,        permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffDocuments",      url: "/staff-portal/documents",     icon: FileText,        permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffAppraisals",     url: "/staff-portal/appraisals",    icon: TrendingUp,      permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffClaims",         url: "/staff-portal/claims",        icon: ClipboardPen,    permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffMessages",       url: "/staff-portal/messages",      icon: MessageSquare,   permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffAnnouncements",  url: "/staff-portal/announcements", icon: Megaphone,       permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffDirectory",      url: "/staff-portal/directory",     icon: BookUser,        permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffDuties",         url: "/staff-portal/duties",        icon: ShieldCheckIcon, permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffCalendar",       url: "/staff-portal/calendar",      icon: Calendar,        permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffBookings",       url: "/staff-portal/bookings",      icon: CalendarClock,   permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffWelfare",        url: "/staff-portal/welfare",       icon: Heart,           permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffTraining",       url: "/staff-portal/training",      icon: Award,           permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffGrievances",     url: "/staff-portal/grievances",    icon: MessageSquare,   permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffHealthSafety",   url: "/staff-portal/health-safety", icon: HeartPulse,      permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffDependants",     url: "/staff-portal/dependants",    icon: Users,           permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffLoans",          url: "/staff-portal/loans",         icon: Wallet,          permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffHelpdesk",       url: "/staff-portal/helpdesk",      icon: Headphones,      permission: P.STAFF_PORTAL_READ },
      { labelKey: "staffProfile",        url: "/staff-portal/profile",       icon: UserCircle,      permission: P.STAFF_PORTAL_READ },
    ],
  },
  {
    titleKey: "driverPortal",
    url: "/driver-portal",
    icon: Bus,
    permission: P.DRIVER_PORTAL_READ,
    children: [
      { labelKey: "driverDashboard",  url: "/driver-portal",              icon: LayoutDashboard, permission: P.DRIVER_PORTAL_READ },
      { labelKey: "myTrips",          url: "/driver-portal/trips",        icon: CalendarClock,   permission: P.DRIVER_PORTAL_READ },
      { labelKey: "myVehicle",        url: "/driver-portal/vehicle",      icon: Truck,           permission: P.DRIVER_PORTAL_READ },
      { labelKey: "preTrip",          url: "/driver-portal/checklist",    icon: ClipboardCheck,  permission: P.DRIVER_PORTAL_READ },
      { labelKey: "incidents",        url: "/driver-portal/incidents",    icon: ShieldAlert,     permission: P.DRIVER_PORTAL_READ },
      { labelKey: "earnings",         url: "/driver-portal/earnings",     icon: DollarSign,      permission: P.DRIVER_PORTAL_READ },
      { labelKey: "performance",      url: "/driver-portal/performance",  icon: TrendingUp,      permission: P.DRIVER_PORTAL_READ },
      { labelKey: "messages",         url: "/driver-portal/messages",     icon: MessageSquare,   permission: P.DRIVER_PORTAL_READ },
      { labelKey: "myProfile",        url: "/driver-portal/profile",      icon: UserCircle,      permission: P.DRIVER_PORTAL_READ },
    ],
  },
  {
    titleKey: "librarianPortal",
    url: "/librarian-portal",
    icon: LibraryBig,
    permission: P.LIBRARIAN_PORTAL_READ,
    children: [
      { labelKey: "librarianDashboard",      url: "/librarian-portal",                  icon: LayoutDashboard,  permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianCatalog",        url: "/librarian-portal/catalog",          icon: BookOpen,         permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianCirculation",    url: "/librarian-portal/circulation",      icon: ScanBarcode,      permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianOverdues",       url: "/librarian-portal/overdues",         icon: BookX,            permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianHolds",          url: "/librarian-portal/holds",            icon: Bookmark,         permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianMembers",        url: "/librarian-portal/members",          icon: Users,            permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianFines",          url: "/librarian-portal/fines",            icon: DollarSign,       permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianTextbooks",      url: "/librarian-portal/textbooks",        icon: BookOpenCheck,    permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianAcquisitions",   url: "/librarian-portal/acquisitions",     icon: ShoppingCart,     permission: P.LIBRARIAN_PORTAL_WRITE },
      { labelKey: "librarianSerials",        url: "/librarian-portal/serials",          icon: Newspaper,        permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianDigital",        url: "/librarian-portal/digital",          icon: Monitor,          permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianStocktake",      url: "/librarian-portal/stocktake",        icon: ClipboardX,       permission: P.LIBRARIAN_PORTAL_WRITE },
      { labelKey: "librarianReadingPrograms", url: "/librarian-portal/reading-programs", icon: Gamepad2,         permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianReviews",        url: "/librarian-portal/reviews",          icon: MessageCircle,    permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianSpaces",         url: "/librarian-portal/spaces",           icon: Building2,        permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianReports",        url: "/librarian-portal/reports",          icon: BarChart3,        permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianLabels",         url: "/librarian-portal/labels",           icon: QrCode,           permission: P.LIBRARIAN_PORTAL_WRITE },
      { labelKey: "librarianILL",            url: "/librarian-portal/ill",              icon: ArrowRightLeft,   permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianCurriculum",     url: "/librarian-portal/curriculum",       icon: GradCapIcon,      permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianEvents",         url: "/librarian-portal/events",           icon: CalendarHeart,    permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianNotifications",  url: "/librarian-portal/notifications",    icon: Send,             permission: P.LIBRARIAN_PORTAL_READ },
      { labelKey: "librarianSettings",       url: "/librarian-portal/settings",         icon: SlidersHorizontal, permission: P.LIBRARIAN_PORTAL_WRITE },
      { labelKey: "librarianProfile",        url: "/librarian-portal/profile",          icon: UserSquare2,      permission: P.LIBRARIAN_PORTAL_READ },
    ],
  },
  {
    titleKey: "instructorPortal",
    url: "/instructor-portal",
    icon: Presentation,
    permission: P.INSTRUCTOR_PORTAL_READ,
    children: [
      { labelKey: "instructorDashboard",    url: "/instructor-portal",               icon: LayoutDashboard,  permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorTimetable",    url: "/instructor-portal/timetable",     icon: Clock,            permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorClasses",      url: "/instructor-portal/classes",       icon: Users,            permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorAttendance",   url: "/instructor-portal/attendance",    icon: CalendarCheck,    permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorGradebook",    url: "/instructor-portal/gradebook",     icon: BookOpen,         permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorAssessments",  url: "/instructor-portal/assessments",   icon: ClipboardList,    permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorAssignments",  url: "/instructor-portal/assignments",   icon: FileText,         permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorExams",        url: "/instructor-portal/exams",         icon: ClipboardCheck,   permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorReportCards",  url: "/instructor-portal/report-cards",  icon: ReceiptText,      permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorPerformance",  url: "/instructor-portal/performance",   icon: TrendingUp,       permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorMessages",     url: "/instructor-portal/messages",      icon: MessageSquare,    permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorLeave",        url: "/instructor-portal/leave",         icon: Briefcase,        permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorDiscipline",   url: "/instructor-portal/discipline",    icon: Shield,           permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorResources",    url: "/instructor-portal/resources",     icon: FolderOpen,       permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorPD",           url: "/instructor-portal/pd",            icon: Award,            permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorCalendar",     url: "/instructor-portal/calendar",      icon: Calendar,         permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorParents",      url: "/instructor-portal/parents",       icon: Contact,          permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorClubs",        url: "/instructor-portal/clubs",         icon: Trophy,           permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorEarnings",     url: "/instructor-portal/earnings",      icon: Wallet,           permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorSubstitutions", url: "/instructor-portal/substitutions", icon: Repeat2,         permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorDuties",       url: "/instructor-portal/duties",        icon: ShieldCheckIcon,  permission: P.INSTRUCTOR_PORTAL_READ },
      { labelKey: "instructorProfile",      url: "/instructor-portal/profile",       icon: UserCircle,       permission: P.INSTRUCTOR_PORTAL_READ },
    ],
  },
  {
    titleKey: "guardianPortal",
    url: "/guardian-portal",
    icon: Heart,
    permission: P.GUARDIAN_PORTAL_READ,
    children: [
      { labelKey: "guardianDashboard",   url: "/guardian-portal",             icon: LayoutDashboard,    permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianChildren",    url: "/guardian-portal/children",    icon: Users,              permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianAcademics",   url: "/guardian-portal/academics",   icon: GraduationCap,      permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianAttendance",  url: "/guardian-portal/attendance",  icon: CalendarCheck,      permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianTimetable",   url: "/guardian-portal/timetable",   icon: Clock,              permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianExams",       url: "/guardian-portal/exams",       icon: ClipboardCheck,     permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianFees",        url: "/guardian-portal/fees",        icon: CreditCard,         permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianMessages",    url: "/guardian-portal/messages",    icon: MessageSquare,      permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianMeetings",    url: "/guardian-portal/meetings",    icon: Contact,            permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianCalendar",    url: "/guardian-portal/calendar",    icon: Calendar,           permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianDiscipline",  url: "/guardian-portal/discipline",  icon: Shield,             permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianTransport",   url: "/guardian-portal/transport",   icon: Bus,                permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianHostel",      url: "/guardian-portal/hostel",      icon: Home,               permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianMeals",       url: "/guardian-portal/meals",       icon: UtensilsCrossed,    permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianLibrary",     url: "/guardian-portal/library",     icon: Library,            permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianClubs",       url: "/guardian-portal/clubs",       icon: Trophy,             permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianHealth",      url: "/guardian-portal/health",      icon: Stethoscope,        permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianLeave",       url: "/guardian-portal/leave",       icon: DoorOpen,           permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianDocuments",   url: "/guardian-portal/documents",   icon: FileText,           permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianProfile",     url: "/guardian-portal/profile",     icon: UserCircle,         permission: P.GUARDIAN_PORTAL_READ },
      { labelKey: "guardianSurveys",     url: "/guardian-portal/surveys",     icon: ClipboardList,      permission: P.GUARDIAN_PORTAL_READ },
    ],
  },
];

const rootPortalNav: NavItem[] = [
  {
    titleKey: "rootPortal",
    url: "/root-portal",
    icon: Globe,
    permission: P.ROOT_PORTAL_READ,
    children: [
      { labelKey: "rootDashboard",   url: "/root-portal",                   icon: LayoutDashboard, permission: P.ROOT_PORTAL_READ },
      { labelKey: "rootTenants",     url: "/root-portal/tenants",           icon: Building2,       permission: P.PLATFORM_TENANTS_READ },
      { labelKey: "rootAnalytics",   url: "/root-portal/analytics",         icon: TrendingUp,      permission: P.PLATFORM_ANALYTICS_READ },
      { labelKey: "rootSystemHealth",url: "/root-portal/system-health",     icon: Activity,        permission: P.ROOT_PORTAL_READ },
      { labelKey: "rootStorage",     url: "/root-portal/storage",           icon: HardDrive,       permission: P.ROOT_PORTAL_READ },
      { labelKey: "rootGlobalUsers", url: "/root-portal/global-users",      icon: Users,           permission: P.PLATFORM_TENANTS_READ },
      { labelKey: "rootBilling",     url: "/root-portal/billing",           icon: DollarSign,      permission: P.PLATFORM_BILLING_READ },
      { labelKey: "rootAuditLog",    url: "/root-portal/audit",             icon: ScrollText,      permission: P.PLATFORM_AUDIT_READ },
      { labelKey: "rootConfig",      url: "/root-portal/config",            icon: ToggleLeft,      permission: P.PLATFORM_CONFIG_READ },
      { labelKey: "rootSupport",     url: "/root-portal/support",           icon: LifeBuoy,        permission: P.PLATFORM_SUPPORT_READ },
      { labelKey: "rootSecurity",    url: "/root-portal/security",          icon: ShieldAlert,     permission: P.PLATFORM_SECURITY_READ },
      { labelKey: "rootReleases",    url: "/root-portal/releases",          icon: Rocket,          permission: P.PLATFORM_RELEASES_READ },
      { labelKey: "rootRoles",       url: "/root-portal/roles",             icon: ShieldCheck,     permission: P.PLATFORM_CONFIG_READ },
      { labelKey: "rootPermissions", url: "/root-portal/permissions",       icon: KeyRound,        permission: P.PLATFORM_CONFIG_READ },
      { labelKey: "rootComms",       url: "/root-portal/communications",    icon: MessageSquare,   permission: P.ROOT_PORTAL_READ },
    ],
  },
];

const heNav: NavItem[] = [
  {
    titleKey: "heFaculties",
    url: "/he/faculties",
    icon: Building2,
    permission: P.CLASSES_READ,
  },
  {
    titleKey: "heProgrammes",
    url: "/he/programmes",
    icon: GraduationCap,
    permission: P.CLASSES_READ,
    children: [
      { labelKey: "heProgrammeList",  url: "/he/programmes",       icon: GraduationCap,  permission: P.CLASSES_READ },
      { labelKey: "heDegrees",        url: "/he/degrees",          icon: Award,          permission: P.CLASSES_READ },
      { labelKey: "heDegreeAudit",    url: "/he/degree-audit",     icon: ClipboardCheck, permission: P.CLASSES_READ },
    ],
  },
  {
    titleKey: "heCourses",
    url: "/he/courses",
    icon: BookOpen,
    permission: P.CLASSES_READ,
    children: [
      { labelKey: "heCourseList",    url: "/he/courses",          icon: BookOpen,     permission: P.CLASSES_READ },
      { labelKey: "heOfferings",     url: "/he/offerings",        icon: CalendarDays, permission: P.CLASSES_READ },
      { labelKey: "heEnrolments",    url: "/he/enrolments",       icon: UserCheck,    permission: P.CLASSES_READ },
    ],
  },
  {
    titleKey: "heResearch",
    url: "/he/research",
    icon: FlaskConical,
    permission: P.CLASSES_READ,
    children: [
      { labelKey: "heResearchEnrolments", url: "/he/research",            icon: FlaskConical, permission: P.CLASSES_READ },
      { labelKey: "heThesis",             url: "/he/thesis",              icon: ScrollText,   permission: P.CLASSES_READ },
      { labelKey: "heDefences",           url: "/he/defences",            icon: Shield,       permission: P.CLASSES_READ },
      { labelKey: "heGradeAppeals",       url: "/he/grade-appeals",       icon: Scale,        permission: P.CLASSES_READ },
    ],
  },
  {
    titleKey: "heConvocation",
    url: "/he/convocation",
    icon: Award,
    permission: P.CLASSES_READ,
    children: [
      { labelKey: "heConvocationList", url: "/he/convocation",   icon: Award,        permission: P.CLASSES_READ },
      { labelKey: "heConferrals",      url: "/he/conferrals",    icon: FileBadge,    permission: P.CLASSES_READ },
    ],
  },
  {
    titleKey: "heFinancialAid",
    url: "/he/financial-aid",
    icon: DollarSign,
    permission: P.FEES_READ,
    children: [
      { labelKey: "heAidSchemes", url: "/he/financial-aid/schemes", icon: Layers,   permission: P.FEES_READ },
      { labelKey: "heAidAwards",  url: "/he/financial-aid/awards",  icon: Award,    permission: P.FEES_READ },
    ],
  },
  {
    titleKey: "heTranscripts",
    url: "/he/transcripts",
    icon: FileText,
    permission: P.REPORTS_READ,
  },
];

const bottomNav: NavItem[] = [
  {
    titleKey: "tenant", url: "/tenant", icon: School, permission: P.SETTINGS_MANAGE,
    children: [
      { labelKey: "tenantProfile",  url: "/tenant",             icon: Building2, permission: P.SETTINGS_MANAGE },
      { labelKey: "schoolsManage",  url: "/tenant/schools",     icon: School,    permission: "schools:read" },
      { labelKey: "departments",    url: "/tenant/departments", icon: Building2, permission: "schools:read" },
      { labelKey: "orgChart",       url: "/tenant/org-chart",   icon: Network,   permission: "schools:read" },
    ],
  },
  {
    titleKey: "users",
    url: "/users",
    icon: UserCog,
    permission: P.USERS_READ,
    children: [
      { labelKey: "allUsers", url: "/users", icon: Users, permission: P.USERS_READ },
      { labelKey: "roles", url: "/users/roles", icon: ShieldCheck, permission: P.ROLES_READ },
      { labelKey: "auditLog", url: "/audit", icon: ScrollText, permission: P.AUDIT_LOGS_READ },
    ],
  },
  { titleKey: "settings", url: "/settings", icon: Settings, permission: P.SETTINGS_READ },
  { titleKey: "support", url: "/support", icon: LifeBuoy },
];

// ── Fallback titles (when i18n key not resolved) ───────────────────────────

const TITLE_FALLBACKS: Record<string, string> = {
  onlineApplication: "Online Application",
  admissionsPortal: "Admissions Portal",
  applicantDashboard: "Dashboard",
  applicantApplications: "My Applications",
  applicantMessages: "Messages",
  applicantNotifications: "Notifications",
  applicantSchoolInfo: "School Info",
  applicantHelp: "Help & FAQ",
  applicantPayment: "Payments",
  applicantReapply: "Re-Apply",
  applicantSecurity: "Security",
  applicantFamily: "Family Account",
  applicantReferees: "Recommendations",
  applicantScheduleTest: "Schedule Test",
  applicantScheduleInterview: "Schedule Interview",
  applicantNewApplication: "Start New Application",
  applicantProfile: "My Profile",
  parentPortal: "Parent Portal",
  studentPortal: "Student Portal",
  studentDashboard: "Dashboard",
  studentGrades: "My Grades",
  studentTimetable: "Timetable",
  studentAttendance: "Attendance",
  studentFees: "Fees & Payments",
  studentExams: "Examinations",
  studentLibrary: "Library",
  studentMessages: "Messages",
  studentDocuments: "Documents",
  studentProfile: "My Profile",
  studentAssignments: "Assignments",
  studentGradeCalc: "Grade Calculator",
  studentStudyPlanner: "Study Planner",
  studentTransport: "Transport",
  studentHostel: "Hostel & Boarding",
  studentClubs: "Clubs & Activities",
  studentCalendar: "School Calendar",
  studentIdCard: "Student ID",
  studentMeals: "Meal Plan",
  studentCounseling: "Counseling",
  studentLeave: "Leave Requests",
  studentGoals: "Academic Goals",
  studentStudyGroups: "Study Groups",
  instructorPortal: "Instructor Portal",
  instructorDashboard: "Dashboard",
  instructorTimetable: "My Timetable",
  instructorClasses: "My Classes",
  instructorAttendance: "Attendance",
  instructorGradebook: "Gradebook",
  instructorAssessments: "Assessments",
  instructorAssignments: "Assignments",
  instructorExams: "Examinations",
  instructorReportCards: "Report Cards",
  instructorPerformance: "Student Performance",
  instructorMessages: "Messages",
  instructorLeave: "Leave Management",
  instructorDiscipline: "Discipline",
  instructorResources: "Resources",
  instructorPD: "Professional Dev",
  instructorCalendar: "Calendar",
  instructorParents: "Parents",
  instructorClubs: "Clubs",
  instructorEarnings: "Earnings",
  instructorSubstitutions: "Substitutions",
  instructorDuties: "Duty Roster",
  instructorProfile: "My Profile",
  guardianPortal: "Guardian Portal",
  guardianDashboard: "Dashboard",
  guardianChildren: "My Children",
  guardianAcademics: "Academic Progress",
  guardianAttendance: "Attendance",
  guardianTimetable: "Timetable",
  guardianExams: "Exams & Results",
  guardianFees: "Fees & Payments",
  guardianMessages: "Messages",
  guardianMeetings: "Meetings",
  guardianCalendar: "School Calendar",
  guardianDiscipline: "Discipline",
  guardianTransport: "Transport",
  guardianHostel: "Hostel & Boarding",
  guardianMeals: "Meal Plan",
  guardianLibrary: "Library",
  guardianClubs: "Clubs & Activities",
  guardianHealth: "Health & Medical",
  guardianLeave: "Absence Requests",
  guardianDocuments: "Documents",
  guardianProfile: "My Profile",
  guardianSurveys: "Feedback & Surveys",
  staffPortal: "Staff Portal",
  staffDashboard: "Dashboard",
  staffPayslips: "Payslips",
  staffLeave: "Leave Management",
  staffDocuments: "Documents",
  staffAppraisals: "Appraisals",
  staffClaims: "Overtime & Claims",
  staffMessages: "Messages",
  staffAnnouncements: "Announcements",
  staffDirectory: "Staff Directory",
  staffDuties: "Duty Roster",
  staffCalendar: "School Calendar",
  staffBookings: "Meetings & Bookings",
  staffWelfare: "Welfare & Benefits",
  staffTraining: "Training & Development",
  staffGrievances: "HR Requests",
  staffHealthSafety: "Health & Safety",
  staffDependants: "Dependant Fees",
  staffLoans: "Advances & Loans",
  staffHelpdesk: "Helpdesk",
  staffProfile: "My Profile",
  librarianPortal: "Librarian Portal",
  librarianDashboard: "Dashboard",
  librarianCatalog: "Catalog",
  librarianBookDetail: "Book Details",
  librarianCatalogEdit: "Add / Edit Book",
  librarianCirculation: "Circulation Desk",
  librarianOverdues: "Overdue Tracker",
  librarianHolds: "Holds & Reservations",
  librarianMembers: "Members",
  librarianFines: "Fines",
  librarianTextbooks: "Textbook Tracker",
  librarianAcquisitions: "Acquisitions",
  librarianSerials: "Serials & Periodicals",
  librarianDigital: "Digital Resources",
  librarianStocktake: "Inventory / Stocktake",
  librarianReadingPrograms: "Reading Programs",
  librarianReviews: "Reviews & Ratings",
  librarianSpaces: "Space Booking",
  librarianReports: "Reports & Analytics",
  librarianLabels: "Barcodes & Labels",
  librarianILL: "Inter-Library Loans",
  librarianCurriculum: "Curriculum Alignment",
  librarianEvents: "Events & Displays",
  librarianNotifications: "Notifications",
  librarianSettings: "Settings & Policies",
  librarianProfile: "My Profile",
  driverPortal: "Driver Portal",
  driverDashboard: "Dashboard",
  myTrips: "My Trips",
  myVehicle: "My Vehicle",
  preTrip: "Pre-Trip Check",
  earnings: "Earnings",
  performance: "Performance",
  myProfile: "My Profile",
  studentManifest: "Student Manifest",
  heFaculties: "Faculties",
  heProgrammes: "Programmes",
  heProgrammeList: "Programmes",
  heDegrees: "Degrees",
  heDegreeAudit: "Degree Audit",
  heCourses: "Courses",
  heCourseList: "Course Catalogue",
  heOfferings: "Course Offerings",
  heEnrolments: "Enrolments",
  heResearch: "Research",
  heResearchEnrolments: "Research Students",
  heThesis: "Thesis Submissions",
  heDefences: "Thesis Defences",
  heGradeAppeals: "Grade Appeals",
  heConvocation: "Convocation",
  heConvocationList: "Convocations",
  heConferrals: "Degree Conferrals",
  heFinancialAid: "Financial Aid",
  heAidSchemes: "Aid Schemes",
  heAidAwards: "Aid Awards",
  heTranscripts: "Transcripts",
  rootPortal: "Root Portal",
  rootDashboard: "Platform Overview",
  rootTenants: "Tenant Management",
  rootAnalytics: "Platform Analytics",
  rootSystemHealth: "System Health",
  rootStorage: "Storage & Backups",
  rootGlobalUsers: "Global Users",
  rootBilling: "Billing & Subscriptions",
  rootAuditLog: "Audit Log",
  rootConfig: "Feature Flags & Config",
  rootSupport: "Support & Helpdesk",
  rootSecurity: "Security & Compliance",
  rootReleases: "Releases & Deployments",
  rootRoles: "Roles",
  rootPermissions: "Permissions",
  rootComms: "Communications",
  dashboard: "Dashboard",
  students: "Students",
  academics: "Academics",
  assessments: "Assessments",
  attendance: "Attendance",
  fees: "Fees",
  timetable: "Timetable",
  events: "Events",
  communications: "Communications",
  notificationProviders: "Notification Providers",
  deliveryQueue: "Delivery Queue",
  providerHealth: "Provider Health",
  hr: "HR",
  admissions: "Admissions",
  library: "Library",
  transport: "Transport",
  compliance: "Vehicle Compliance",
  tripSchedule: "Bulk Schedule",
  drivers: "Drivers",
  fuelAnalytics: "Fuel Analytics",
  transportAlerts: "Parent Alerts",
  routeDetail: "Route Details",
  facilities: "Facilities",
  hostel: "Hostel",
  hostelOverview: "Overview",
  hostelManagement: "Manage Hostels",
  hostelMaintenance: "Maintenance",
  hostelVisitors: "Visitor Log",
  hostelExeats: "Exeat & Leave",
  hostelFees: "Hostel Fees",
  hostelNotices: "Notices",
  hostelInspections: "Inspections",
  hostelRollCall: "Roll Call",
  hostelIncidents: "Incidents",
  inventory: "Inventory",
  inventoryOverview: "Overview",
  items: "Items",
  inventoryIssues: "Issues & Returns",
  inventoryMovements: "Stock Movements",
  stocktake: "Stock Take",
  warehouses: "Warehouses",
  assetRegister: "Asset Register",
  disposals: "Disposals",
  inventoryReports: "Reports",
  suppliers: "Suppliers",
  purchaseOrders: "Purchase Orders",
  supplierDetails: "Supplier Details",
  purchaseOrderDetail: "PO Details",
  itemDetail: "Item Detail",
  health: "Health",
  discipline: "Discipline",
  analytics: "Analytics",
  reports: "Reports",
  audit: "Audit Log",
  tenant: "School",
  tenantProfile: "Organisation Profile",
  schoolsManage: "Schools",
  departments: "Departments",
  orgChart: "Org Chart",
  users: "Users",
  settings: "Settings",
  support: "Help & Support",
};

// ── Helpers ────────────────────────────────────────────────────────────────

function usePermissions() {
  return useAuthStore((s) => s.permissions);
}

function canSee(permissions: string[], permission?: string) {
  if (!permission) return true;
  return permissions.some((held) => hasPermission(held, permission));
}

function isChildActive(url: string, pathname: string) {
  // Exact-match-only parents (to avoid /health matching /health/visits,
  // or /root-portal matching every root-portal/* sub-page)
  const exactOnly = ["/attendance", "/library", "/health", "/assessments", "/root-portal"];
  if (exactOnly.includes(url)) return pathname === url;
  return pathname === url || pathname.startsWith(url + "/");
}

// ── Sub-item ───────────────────────────────────────────────────────────────

function SubNavItem({ item }: { item: SubItem }) {
  const { pathname } = useLocation();
  const permissions = usePermissions();
  const { t } = useTranslation("nav");

  if (!canSee(permissions, item.permission)) return null;

  const active = isChildActive(item.url, pathname);
  const label = t(item.labelKey, TITLE_FALLBACKS[item.labelKey] ?? item.labelKey);

  return (
    <li>
      <Link
        to={item.url}
        data-active={active}
        className={cn(
          "sidebar-sub-item-link group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-all duration-150",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          active
            ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
            : "text-sidebar-foreground/65 hover:text-sidebar-foreground"
        )}
      >
        <item.icon
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            active
              ? "text-sidebar-primary"
              : "text-sidebar-foreground/35 group-hover:text-sidebar-foreground/60"
          )}
        />
        <span className="flex-1 truncate">{label}</span>
        {active && <span className="sidebar-active-dot h-1.5 w-1.5 rounded-full bg-sidebar-primary shrink-0" />}
      </Link>
    </li>
  );
}

// ── Nav item (with optional accordion) ────────────────────────────────────

function NavItemRow({ item }: { item: NavItem }) {
  const { pathname } = useLocation();
  const permissions = usePermissions();
  const { state: sidebarState } = useSidebar();
  const collapsed = sidebarState === "collapsed";
  const { t } = useTranslation("nav");

  const raw = t(item.titleKey);
  const title = raw === item.titleKey ? (TITLE_FALLBACKS[item.titleKey] ?? item.titleKey) : raw;

  if (!canSee(permissions, item.permission)) return null;

  const visibleChildren = (item.children ?? []).filter((c) => canSee(permissions, c.permission));
  const hasChildren = visibleChildren.length > 0;

  const isParentActive =
    item.url === "/" ? pathname === "/" : pathname === item.url || pathname.startsWith(item.url + "/");
  const isAnyChildActive = visibleChildren.some((c) => isChildActive(c.url, pathname));
  const active = isParentActive || isAnyChildActive;

  const [open, setOpen] = React.useState(isAnyChildActive);
  React.useEffect(() => {
    if (isAnyChildActive) setOpen(true);
  }, [isAnyChildActive]);

  const iconBox = (
    <span
      className={cn(
        "nav-icon-box flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors duration-150",
        active
          ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
          : "bg-sidebar-accent/50 text-sidebar-foreground/50 group-hover/btn:bg-sidebar-accent group-hover/btn:text-sidebar-foreground"
      )}
    >
      <item.icon className="h-3.5 w-3.5" />
    </span>
  );

  // Collapsed: icon only, tooltip shows children as links
  if (collapsed) {
    const trigger = (
      <SidebarMenuButton
        isActive={active}
        tooltip={title}
        className="group/btn h-9 gap-3 rounded-lg"
        onClick={hasChildren ? () => setOpen((o) => !o) : undefined}
        asChild={!hasChildren}
      >
        {hasChildren ? (
          iconBox
        ) : item.external ? (
          <a href={item.url} target="_blank" rel="noreferrer">{iconBox}</a>
        ) : (
          <Link to={item.url}>{iconBox}</Link>
        )}
      </SidebarMenuButton>
    );

    if (!hasChildren) return <SidebarMenuItem>{trigger}</SidebarMenuItem>;

    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right" className="p-2 min-w-32">
            <p className="mb-1 text-xs font-semibold">{title}</p>
            <div className="flex flex-col gap-0.5">
              {visibleChildren.map((c) => (
                <Link
                  key={c.labelKey}
                  to={c.url}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t(c.labelKey, TITLE_FALLBACKS[c.labelKey] ?? c.labelKey)}
                </Link>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  // Expanded — no children: simple link button
  if (!hasChildren) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={active}
          tooltip={title}
          className="group/btn h-9 gap-3 rounded-lg"
        >
          {item.external ? (
            <a href={item.url} target="_blank" rel="noreferrer">
              {iconBox}
              <span className="truncate text-[13px]">{title}</span>
            </a>
          ) : (
            <Link to={item.url}>
              {iconBox}
              <span className="truncate text-[13px]">{title}</span>
            </Link>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  // Expanded — with children: accordion trigger
  return (
    <SidebarMenuItem>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "group/btn flex h-9 w-full items-center gap-3 rounded-lg px-2 text-[13px] transition-colors duration-150",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          active ? "text-sidebar-foreground font-medium" : "text-sidebar-foreground/75"
        )}
      >
        {iconBox}
        <span className="flex-1 truncate text-left">{title}</span>
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-sidebar-foreground/30 transition-transform duration-200",
            open && "rotate-90"
          )}
        />
      </button>

      {/* Collapsible children */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          open ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        )}
      >
        <ul className="sidebar-sub-list ml-3.5 mt-0.5 space-y-0.5 border-l border-sidebar-border/40 pl-3 pb-1">
          {visibleChildren.map((child) => (
            <React.Fragment key={child.labelKey}>
              {child.group && (
                <li className="pt-2 pb-0.5">
                  <span className="block px-2.5 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/30 select-none">
                    {child.group}
                  </span>
                </li>
              )}
              <SubNavItem item={child} />
            </React.Fragment>
          ))}
        </ul>
      </div>
    </SidebarMenuItem>
  );
}

// ── Section group ──────────────────────────────────────────────────────────

function NavSection({ label, items }: { label: string; items: NavItem[] }) {
  const permissions = usePermissions();
  const { state: sidebarState } = useSidebar();
  const collapsed = sidebarState === "collapsed";

  const visible = items.filter((item) => canSee(permissions, item.permission));
  if (visible.length === 0) return null;

  return (
    <div className="px-2 py-1">
      {!collapsed && (
        <p className="sidebar-section-label mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/30 select-none">
          {label}
        </p>
      )}
      <SidebarMenu className="gap-0.5">
        {visible.map((item) => (
          <NavItemRow key={item.titleKey} item={item} />
        ))}
      </SidebarMenu>
    </div>
  );
}

// ── App Sidebar ────────────────────────────────────────────────────────────

const SIDEBAR_VARIANT_MAP: Record<SidebarStyle, "sidebar" | "floating" | "inset"> = {
  inset:    "inset",
  floating: "floating",
  minimal:  "sidebar",
  bold:     "sidebar",
  neon:     "sidebar",
  mac:      "sidebar",
};

export function AppSidebar() {
  const lockSession = useAuthStore((s) => s.lock);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const sidebarStyle = useUIStore((s) => s.sidebarStyle);
  const { data: tenant } = useTenant();
  const isHigherEd = tenant ? HE_SCHOOL_TYPES.has(tenant.school_type) : false;

  const tenantId = user?.tenant_id ?? "";
  const onlineApplicationItem: NavItem = {
    titleKey: "onlineApplication",
    url: tenantId ? `/apply/${tenantId}` : "/apply",
    icon: FileText,
    permission: P.ADMISSIONS_READ,
    external: true,
  };
  const admissionsPortalItem: NavItem = {
    titleKey: "admissionsPortal",
    url: "/admissions-portal/dashboard",
    icon: UserPlus,
    permission: P.APPLICANT_PORTAL_READ,
    children: [
      { labelKey: "applicantDashboard",      url: "/admissions-portal/dashboard",      icon: LayoutDashboard, permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantApplications",   url: "/admissions-portal/applications",  icon: FileBadge,       permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantMessages",       url: "/admissions-portal/messages",      icon: MessageSquare,   permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantNotifications",  url: "/admissions-portal/notifications", icon: Bell,            permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantSchoolInfo",     url: "/admissions-portal/school-info",   icon: School,          permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantHelp",           url: "/admissions-portal/help",          icon: LifeBuoy,        permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantPayment",        url: "/admissions-portal/payments",      icon: CreditCard,      permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantReapply",        url: "/admissions-portal/reapply",       icon: Repeat,          permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantFamily",         url: "/admissions-portal/family",        icon: Users,           permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantReferees",       url: "/admissions-portal/referees",      icon: Award,           permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantClaim",          url: "/admissions-portal/claim",         icon: ClipboardCheck,  permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantNewApplication", url: "/admissions-portal/apply",         icon: Send,            permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantSecurity",       url: "/admissions-portal/security",      icon: ShieldCheck,     permission: P.APPLICANT_PORTAL_READ },
      { labelKey: "applicantProfile",        url: "/admissions-portal/profile",       icon: UserCircle,      permission: P.APPLICANT_PORTAL_READ },
    ],
  };
  const allPortalNav: NavItem[] = [onlineApplicationItem, admissionsPortalItem, ...portalNav];

  const initials = user
    ? (user.full_name ?? "U")
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "U";

  return (
    <Sidebar collapsible="icon" variant={SIDEBAR_VARIANT_MAP[sidebarStyle] ?? "inset"}>
      {/* Brand */}
      <SidebarHeader className="pb-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent active:bg-transparent">
              <Link to="/">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-sidebar-primary to-sidebar-primary/60 shadow-md">
                  <GraduationCap className="h-4 w-4 text-white" />
                </div>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-bold tracking-tight">{t("app.name")}</span>
                  <span className="truncate text-[10px] text-sidebar-foreground/45">{t("app.tagline")}</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Nav */}
      <SidebarContent className="gap-0 overflow-x-hidden">
        <NavSection label={t("sidebar.main")} items={mainNav} />
        <SidebarSeparator className="mx-2 my-1" />
        <NavSection label={t("sidebar.modules")} items={secondaryNav} />
        <SidebarSeparator className="mx-2 my-1" />
        <NavSection label={t("sidebar.analytics") ?? "Analytics"} items={analyticsNav} />
        {isHigherEd && (
          <>
            <SidebarSeparator className="mx-2 my-1" />
            <NavSection label={t("sidebar.higherEd") ?? "Higher Education"} items={heNav} />
          </>
        )}
        <SidebarSeparator className="mx-2 my-1" />
        <NavSection label={t("sidebar.portals") ?? "Portals"} items={allPortalNav} />
        <SidebarSeparator className="mx-2 my-1" />
        <NavSection label={t("sidebar.rootPortal") ?? "Root Portal"} items={rootPortalNav} />
        <SidebarSeparator className="mx-2 my-1" />
        <NavSection label={t("sidebar.system")} items={bottomNav} />
      </SidebarContent>

      {/* User footer */}
      <SidebarFooter className="border-t border-sidebar-border/50 pt-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="h-auto gap-3 rounded-xl py-2 data-[state=open]:bg-sidebar-accent"
                >
                  <Avatar className="h-8 w-8 rounded-lg shrink-0">
                    <AvatarFallback className="rounded-lg bg-linear-to-br from-sidebar-primary/20 to-sidebar-primary/10 text-xs font-bold text-sidebar-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left leading-tight">
                    <span className="truncate text-sm font-medium">{user?.display_name ?? "User"}</span>
                    <span className="truncate text-[11px] text-sidebar-foreground/45">{user?.email ?? ""}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 text-sidebar-foreground/35" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl p-1"
                side="bottom"
                align="end"
                sideOffset={6}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
                    <Avatar className="h-9 w-9 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-primary/10 text-sm font-bold text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 leading-tight">
                      <span className="truncate text-sm font-semibold">{user?.display_name ?? "User"}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email ?? ""}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings/profile" className="cursor-pointer">
                    <UserCircle className="mr-2 h-4 w-4" />
                    My Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    lockSession();
                    navigate("/locked");
                  }}
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Lock Screen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void logoutWithServer()} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("actions.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
